'use client'

import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import confetti from 'canvas-confetti'
import { motion } from 'motion/react'

import type { RouterOutputs } from '@package/api'
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'

interface BadgeVictoryScreenProps {
  className?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Earned achievement to show (from achievement.getAll). Should be set when opening the modal. */
  achievement: RouterOutputs['achievement']['getAll'][number]
  /** Owner of the achievement being claimed. Defaults to the signed-in user; pass when claiming on another profile (e.g. a parent viewing a child). */
  userId?: string
  /** Optional CTA (e.g. "Prestasiku" button). If not provided, a default "Lanjut" close button is shown. */
  children?: React.ReactNode
}

const bounceSpring = { type: 'spring' as const, stiffness: 400, damping: 12 }
const gentleSpring = { type: 'spring' as const, stiffness: 260, damping: 20 }

export function BadgeVictoryScreen({
  className,
  open,
  onOpenChange,
  achievement,
  userId,
  children,
}: BadgeVictoryScreenProps) {
  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const badge = achievement.achievementBadge
  // Deleted media leaves a truthy-but-dead iconUrl; fall back to the placeholder on load error.
  const [iconFailed, setIconFailed] = useState(false)
  // Reset when the badge changes — this modal instance is reused across achievements.
  useEffect(() => setIconFailed(false), [badge.iconUrl])

  const { mutate: handleClaim } = useMutation(
    trpc.achievement.claim.mutationOptions({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({ queryKey: trpc.achievement.getAll.queryKey({ userId: data.userId }) })
        await queryClient.invalidateQueries({ queryKey: trpc.achievement.getAll.queryKey() })
        await queryClient.invalidateQueries({ queryKey: trpc.achievement.hasUnclaimed.queryKey() })
      },
    }),
  )

  const fireConfetti = useCallback(() => {
    const count = 180
    const defaults = {
      origin: { y: 0.65, x: 0.5 },
      spread: 100,
      ticks: 120,
      gravity: 0.8,
      decay: 0.92,
      startVelocity: 35,
      zIndex: 2502,
      colors: ['#58CC02', '#FFC800', '#1CB0F6', '#FF4B4B', '#CE82FF'],
    }

    function fire(particleRatio: number, opts: confetti.Options) {
      void confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      })
    }

    // Duolingo-style burst from center
    fire(0.3, { spread: 70, startVelocity: 50 })
    fire(0.2, { spread: 55, startVelocity: 45, scalar: 1.1 })
    fire(0.25, { spread: 100, startVelocity: 30, decay: 0.9, scalar: 0.9 })
    fire(0.15, { spread: 120, startVelocity: 25 })
  }, [])

  useEffect(() => {
    if (open) {
      fireConfetti()
    }
  }, [open, fireConfetti])

  // Claim badge when modal opens (e.g. mark as claimed on server)
  useEffect(() => {
    if (open) {
      handleClaim({ achievementId: achievement.id, userId })
    }
  }, [open, handleClaim, achievement.id, userId])

  const showReward = badge.bonusRewardEnabled && badge.bonusXpValue != null && badge.bonusXpValue > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'srd:max-w-sm srd:overflow-hidden srd:border-0 srd:rounded-2xl srd:from-neutrals-nvar-75 srd:to-neutrals-n-100 srd:bg-linear-to-b srd:border-none srd:p-4',
          className,
        )}
        showCloseButton={false}
      >
        <DialogTitle className='srd:sr-only'>Badge Victory Screen</DialogTitle>
        <DialogDescription className='srd:sr-only'>Badge Victory Screen</DialogDescription>

        <motion.div
          className='srd:bg-neutrals-white srd:rounded-lg srd:p-4 srd:flex srd:flex-col srd:items-center srd:gap-10'
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={gentleSpring}
        >
          <section className='srd:flex srd:flex-col srd:items-center'>
            <motion.p
              className='srd:text-center srd:text-xl srd:font-bold srd:text-complementary-black'
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...bounceSpring, delay: 0.05 }}
            >
              Hebat!
            </motion.p>
            <motion.p
              className='srd:text-center srd:text-xs srd:text-complementary-black srd:line-clamp-2'
              initial={{ y: 4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...gentleSpring, delay: 0.1 }}
            >
              Kamu sudah selesai {badge.title}
            </motion.p>
          </section>

          {/* Badge graphic with Duolingo-style pop */}
          <motion.div
            className='srd:relative srd:flex srd:items-center srd:justify-center'
            initial={{ scale: 0, rotate: -12 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ ...bounceSpring, delay: 0.12 }}
          >
            <motion.div
              className='srd:absolute srd:inset-0 srd:rounded-full srd:bg-[#CE82FF]/10'
              style={{ width: 100, height: 100, margin: '0 auto' }}
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: 1.3, opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
            />
            {badge.iconUrl && !iconFailed ? (
              <motion.img
                src={badge.iconUrl}
                alt=''
                className='srd:relative srd:size-50 srd:object-contain'
                style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))' }}
                draggable={false}
                onError={() => setIconFailed(true)}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ ...bounceSpring, delay: 0.14 }}
              />
            ) : (
              <div className='srd:size-50 srd:rounded-full srd:bg-neutrals-n-20 srd:flex srd:items-center srd:justify-center srd:text-4xl srd:font-bold srd:text-neutrals-n-60'>
                ?
              </div>
            )}
          </motion.div>

          <section className='srd:flex srd:flex-col srd:items-center'>
            <motion.p
              className='srd:text-center srd:text-xs srd:text-complementary-black'
              initial={{ y: 4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...gentleSpring, delay: 0.2 }}
            >
              Medali
            </motion.p>

            {/* Badge name: "Tamat 100 Buku" */}
            <motion.p
              className='srd:text-center srd:text-base srd:font-semibold srd:text-complementary-black srd:line-clamp-2'
              initial={{ y: 4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...gentleSpring, delay: 0.22 }}
            >
              {badge.title}
            </motion.p>

            {/* Reward: "+ 15 poin Kosakata" */}
            {showReward && (
              <motion.p
                className='srd:text-center srd:text-xs srd:font-bold srd:text-complementary-purple-75'
                initial={{ y: 4, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ ...gentleSpring, delay: 0.26 }}
              >
                + {badge.bonusXpValue} poin{badge.bonusCoreSkillTag ? ` ${badge.bonusCoreSkillTag.name}` : ''}
              </motion.p>
            )}
          </section>

          {/* Optional CTA (e.g. "Prestasiku" button) or default "Lanjut" */}
          <motion.div
            className='srd:relative srd:z-10 srd:w-full'
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ ...gentleSpring, delay: 0.3 }}
          >
            {children ?? (
              <DialogClose
                render={
                  <Button size='lg' variant='outline' className='srd:w-full'>
                    Lanjut
                  </Button>
                }
              />
            )}
          </motion.div>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
